import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://alarmita.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

type RouteMeta = {
  title: string;
  description: string;
  robots: string;
};

const routeMetadata: Record<string, RouteMeta> = {
  "/": {
    title: "ADT Alarmas - Precios y Promociones en Argentina",
    description:
      "Alarmas ADT para hogar y comercio. Consultá precios, packs con cámaras y monitoreo bonificado hasta 6 meses.",
    robots: "index, follow"
  },
  "/cotizador": {
    title: "Cotizá tu Alarma ADT - Recibí precio al instante",
    description:
      "Completá el formulario y recibí una cotización personalizada para tu alarma ADT en minutos. Sin compromiso.",
    robots: "index, follow"
  },
  "/send": {
    title: "Solicitud recibida - ADT Alarmas",
    description:
      "Gracias por tu solicitud. Nuestro equipo te contactará en minutos con una cotización personalizada.",
    robots: "noindex, follow"
  }
};

const homeStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      name: "Alarmita.com - ADT Agente Oficial",
      url: SITE_URL,
      telephone: "0810-220-3014",
      areaServed: "AR",
      priceRange: "$$",
      description:
        "Distribuidora oficial de alarmas ADT para hogar y comercio en Argentina."
    },
    {
      "@type": "Organization",
      name: "Alarmita.com",
      url: SITE_URL,
      description:
        "Agente oficial de ADT en Argentina con instalación y monitoreo de alarmas para hogar y comercio.",
      sameAs: ["https://wa.me/541126597907", "https://www.adt.com.ar/"]
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "¿Por qué elegir ADT?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "ADT ofrece monitoreo 24 horas, soporte especializado y tecnología avanzada para proteger hogares y comercios."
          }
        },
        {
          "@type": "Question",
          name: "¿Cómo cotizo mi alarma ADT?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Completás tus datos, recibís una cotización personalizada y luego coordinamos la instalación del sistema."
          }
        },
        {
          "@type": "Question",
          name: "¿Qué incluye un pack de seguridad ADT?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Puede incluir panel de control, sensores de movimiento, contactos magnéticos, cámara HD y acceso desde la app ADT."
          }
        }
      ]
    },
    {
      "@type": "HowTo",
      name: "Cómo contratar tu alarma ADT",
      description: "Proceso simple para contratar y activar tu sistema de seguridad ADT.",
      step: [
        {
          "@type": "HowToStep",
          name: "Ingresás la información",
          text: "Completá tus datos para conocer las opciones de seguridad para tu hogar o comercio."
        },
        {
          "@type": "HowToStep",
          name: "Recibís la cotización",
          text: "Un especialista te envía una propuesta personalizada con precios y promociones."
        },
        {
          "@type": "HowToStep",
          name: "Instalamos tu alarma",
          text: "Coordinamos la instalación del sistema para dejar tu propiedad protegida."
        }
      ]
    },
    {
      "@type": "ItemList",
      name: "Packs de seguridad ADT",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@type": "Product",
            name: "Panel Quolsys Touch",
            description:
              "Panel touch para controlar el sistema de seguridad, cámaras y configuraciones."
          }
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@type": "Product",
            name: "Panel Climax",
            description:
              "Panel 100% inalámbrico con botones antipánico y activación del equipo."
          }
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@type": "Product",
            name: "Central con sirena",
            description:
              "Central de alarma con sirena integrada y batería de respaldo."
          }
        },
        {
          "@type": "ListItem",
          position: 4,
          item: {
            "@type": "Product",
            name: "Cámara HD con IA",
            description:
              "Cámara con acceso en vivo, audio, alertas y grabación en la nube."
          }
        },
        {
          "@type": "ListItem",
          position: 5,
          item: {
            "@type": "Product",
            name: "Sensores de movimiento",
            description:
              "Sensores que detectan movimiento y disparan alertas de seguridad."
          }
        },
        {
          "@type": "ListItem",
          position: 6,
          item: {
            "@type": "Product",
            name: "Contactos magnéticos",
            description:
              "Sensores de apertura para puertas y ventanas conectados a la alarma."
          }
        },
        {
          "@type": "ListItem",
          position: 7,
          item: {
            "@type": "Product",
            name: "App ADT Security",
            description:
              "Aplicación móvil para controlar la alarma y recibir notificaciones."
          }
        },
        {
          "@type": "ListItem",
          position: 8,
          item: {
            "@type": "Product",
            name: "Placa ADT",
            description:
              "Señalización exterior para indicar protección activa con sistema ADT."
          }
        }
      ]
    }
  ]
};

const normalizePath = (path: string): string =>
  path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;

const SeoHead = (): JSX.Element => {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const meta = routeMetadata[path] || routeMetadata["/"];
  const canonicalPath = path === "/" ? "" : path;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="es-AR" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta
        property="og:image:alt"
        content="Promociones de alarmas ADT para hogar y comercio"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="es_AR" />
      <meta property="og:site_name" content="Alarmita.com" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      {path === "/" ? <link rel="preload" as="image" href="/hero.avif" type="image/avif" /> : null}
      {path === "/" ? <link rel="preload" as="image" href="/hero.webp" type="image/webp" /> : null}

      {path === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(homeStructuredData)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default SeoHead;
